import { z } from 'zod';

/**
 * Etapas do funil. Os identificadores são os mesmos do protótipo original e são
 * persistidos no DynamoDB — renomear um id é uma migração de dados, não um refactor.
 */
export const ETAPAS_IDS = [
  'cadastro',
  'carta',
  'sinal',
  'curadoria',
  'aprovado',
  'aceite',
  'ativo',
  'recusado',
  'desistiu',
] as const;
export const etapaSchema = z.enum(ETAPAS_IDS);
export type EtapaId = z.infer<typeof etapaSchema>;

/** Cor/semântica do selo na interface. */
export type TomDaEtapa = 'neutro' | 'atencao' | 'positivo' | 'negativo';

export interface Etapa {
  id: EtapaId;
  rotulo: string;
  tom: TomDaEtapa;
  /** Etapas para as quais esta pode avançar. Lista vazia = etapa terminal. */
  proximas: readonly EtapaId[];
}

export const ETAPAS: Record<EtapaId, Etapa> = {
  cadastro: { id: 'cadastro', rotulo: 'Cadastro', tom: 'neutro', proximas: ['carta', 'desistiu'] },
  carta: {
    id: 'carta',
    rotulo: 'Carta emitida',
    tom: 'neutro',
    proximas: ['sinal', 'desistiu'],
  },
  sinal: {
    id: 'sinal',
    rotulo: 'Sinal pago',
    tom: 'atencao',
    proximas: ['curadoria', 'desistiu'],
  },
  curadoria: {
    id: 'curadoria',
    rotulo: 'Em curadoria',
    tom: 'atencao',
    proximas: ['aprovado', 'recusado', 'desistiu'],
  },
  aprovado: {
    id: 'aprovado',
    rotulo: 'Aprovado',
    tom: 'positivo',
    proximas: ['aceite', 'desistiu'],
  },
  aceite: {
    id: 'aceite',
    rotulo: 'Aceite assinado',
    tom: 'positivo',
    proximas: ['ativo', 'desistiu'],
  },
  ativo: { id: 'ativo', rotulo: 'Cadeira ocupada', tom: 'positivo', proximas: [] },
  recusado: { id: 'recusado', rotulo: 'Não aprovado', tom: 'negativo', proximas: [] },
  desistiu: { id: 'desistiu', rotulo: 'Desistiu', tom: 'negativo', proximas: [] },
};

/** Cadeira efetivamente ocupada — entra na receita contratada. */
export const ETAPAS_OCUPADAS: readonly EtapaId[] = ['aceite', 'ativo'];
/** Cadeira pré-reservada pelo sinal, ainda sem contrato assinado. */
export const ETAPAS_PRE_RESERVADAS: readonly EtapaId[] = ['sinal', 'curadoria', 'aprovado'];
/** Saiu do funil. */
export const ETAPAS_ENCERRADAS: readonly EtapaId[] = ['recusado', 'desistiu'];

export function estaNoFunil(etapa: EtapaId): boolean {
  return !ETAPAS_ENCERRADAS.includes(etapa);
}

export function ocupaCadeira(etapa: EtapaId): boolean {
  return ETAPAS_OCUPADAS.includes(etapa);
}

export function preReservaCadeira(etapa: EtapaId): boolean {
  return ETAPAS_PRE_RESERVADAS.includes(etapa);
}

/**
 * Regra única de avanço do funil. O backend recusa qualquer transição que não passe aqui,
 * então o cockpit e a API nunca divergem sobre o que é permitido.
 */
export function podeAvancar(de: EtapaId, para: EtapaId): boolean {
  return ETAPAS[de].proximas.includes(para);
}

/**
 * Etapas que só o gateway de pagamento pode declarar.
 *
 * `sinal` e `ativo` afirmam que dinheiro entrou. Quem afirma isso é o webhook do Asaas,
 * não um clique. Um caminho manual continua existindo para pagamento fora do gateway,
 * mas exige `admin` e justificativa registrada — ver a rota de etapa.
 */
export const ETAPAS_DE_PAGAMENTO: readonly EtapaId[] = ['sinal', 'ativo'];

export function etapaEDePagamento(etapa: EtapaId): boolean {
  return ETAPAS_DE_PAGAMENTO.includes(etapa);
}

/**
 * Fato que cada etapa afirma, e o marco que o comprova.
 *
 * O nome da etapa é uma declaração ao operador e ao cliente: "Carta emitida", "Sinal
 * pago", "Aceite assinado". O sistema não pode declarar o que não sabe — então mover para
 * uma dessas etapas exige o marco correspondente já carimbado.
 *
 * As etapas de fora desta lista são decisões, não fatos: `aprovado` e `recusado` são o
 * juízo da curadoria, `desistiu` é a comunicação do Proponente. Elas se justificam pelo
 * próprio ato de quem as registra, com papel e motivo exigidos na rota.
 */
export const MARCO_QUE_SUSTENTA_A_ETAPA: Partial<Record<EtapaId, MarcoId>> = {
  carta: 'cartaEmitida',
  sinal: 'sinalPago',
  curadoria: 'sinalPago',
  aceite: 'termoAssinadoRecebido',
  ativo: 'saldoPago',
};

/**
 * Marcos registrados na esteira. Cada chave guarda a data ISO em que o marco ocorreu.
 * São histórico, não estado: a etapa é a fonte da verdade sobre a posição no funil.
 */
export const MARCOS_IDS = [
  'cartaEmitida',
  /** O PDF assinado voltou e está guardado. Emitir não é assinar. */
  'cartaAssinadaRecebida',
  'sinalLinkGerado',
  'sinalPago',
  'curadoriaDecidida',
  'termoEmitido',
  'regimentoEmitido',
  'termoAssinadoRecebido',
  'saldoLinkGerado',
  'saldoPago',
  'onboardingRealizado',
] as const;
export const marcoSchema = z.enum(MARCOS_IDS);
export type MarcoId = z.infer<typeof marcoSchema>;

/**
 * Marco que comprova a assinatura de cada documento. Emitir um PDF não prova nada sobre a
 * vontade do Proponente — só o documento assinado de volta prova.
 */
export const MARCO_DE_ASSINATURA = {
  carta: 'cartaAssinadaRecebida',
  termo: 'termoAssinadoRecebido',
  /** O Regimento é anexo do Termo e assina junto com ele. */
  regimento: 'termoAssinadoRecebido',
} as const satisfies Record<string, MarcoId>;
